<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="donation-detail-view">
      <!-- Back Button -->
      <IntercomButton variant="ghost" size="sm" @click="$router.push('/donations')" class="back-button">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        {{ $t('common.back') }}
      </IntercomButton>

      <div v-if="isLoading" class="loading-state">
        {{ $t('common.loading') }}
      </div>

      <template v-else-if="donation">
        <!-- Page Header -->
        <div class="page-header">
          <div class="header-info">
            <h1 class="page-title">{{ donation.donation_number }}</h1>
            <IntercomBadge :variant="getTypeVariant(donation.donation_type)">
              {{ donation.donation_type ? $t(`donation.types.${donation.donation_type}`) : '' }}
            </IntercomBadge>
          </div>
          <div class="header-actions">
            <IntercomButton variant="secondary" @click="showEditModal = true">
              {{ $t('common.edit') }}
            </IntercomButton>
            <IntercomButton variant="primary" @click="showPrintModal = true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {{ $t('receipt.printReceipt') }}
            </IntercomButton>
          </div>
        </div>

        <!-- Stats Row -->
        <div class="stats-row">
          <IntercomCard v-if="donation.donation_type !== 'volunteer'" class="stat-card primary">
            <div class="stat-label">{{ $t('donation.amount') }}</div>
            <div class="stat-value">{{ formatCompactCurrency(donation.amount || 0) }}</div>
          </IntercomCard>
          <IntercomCard v-if="donation.donation_type === 'volunteer' && donation.volunteer_hours" class="stat-card primary">
            <div class="stat-label">{{ $t('donation.volunteerHours') }}</div>
            <div class="stat-value">{{ donation.volunteer_hours }} hrs</div>
          </IntercomCard>
          <IntercomCard class="stat-card">
            <div class="stat-label">{{ $t('donation.date') }}</div>
            <div class="stat-value">{{ formatDate(donation.donation_date) }}</div>
          </IntercomCard>
          <IntercomCard v-if="donation.receipt_number" class="stat-card">
            <div class="stat-label">{{ $t('donation.receiptNumber') }}</div>
            <div class="stat-value receipt-value">{{ donation.receipt_number }}</div>
          </IntercomCard>
          <IntercomCard v-if="donation.bank_account_name" class="stat-card">
            <div class="stat-label">{{ $t('donation.receivedIn') || 'Received In' }}</div>
            <div class="stat-value">{{ donation.bank_account_name }}</div>
          </IntercomCard>
        </div>

        <!-- Patron Card -->
        <IntercomCard class="patron-card" clickable @click="$router.push(`/patrons/${donation.patron_id}`)">
          <div class="patron-card-content">
            <div class="patron-avatar">
              {{ donation.patron_name?.charAt(0)?.toUpperCase() || '?' }}
            </div>
            <div class="patron-info">
              <span class="patron-label">{{ $t('donation.patron') }}</span>
              <span class="patron-name">{{ donation.patron_name }}</span>
            </div>
            <svg class="patron-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </IntercomCard>

        <!-- Details Card -->
        <IntercomCard v-if="donation.fund_type || donation.payment_mode || donation.description || donation.notes" class="details-card">
          <h3 class="details-title">{{ $t('common.details') }}</h3>
          <div v-if="donation.fund_type && donation.donation_type !== 'volunteer'" class="detail-row">
            <span class="detail-label">Fund Type</span>
            <IntercomBadge :variant="donation.fund_type === 'corpus' ? 'warning' : donation.fund_type === 'specific' ? 'info' : 'default'">
              {{ donation.fund_type === 'corpus' ? 'Corpus' : donation.fund_type === 'specific' ? 'Specific' : 'General' }}
            </IntercomBadge>
          </div>
          <div v-if="donation.payment_mode && donation.donation_type === 'money'" class="detail-row">
            <span class="detail-label">Payment Mode</span>
            <span class="detail-value">{{ formatPaymentMode(donation.payment_mode) }}</span>
          </div>
          <div v-if="donation.description" class="detail-row">
            <span class="detail-label">{{ $t('donation.description') }}</span>
            <span class="detail-value">{{ donation.description }}</span>
          </div>
          <div v-if="donation.notes" class="detail-row">
            <span class="detail-label">{{ $t('donation.notes') }}</span>
            <span class="detail-value">{{ donation.notes }}</span>
          </div>
        </IntercomCard>

        <!-- Danger Zone -->
        <div class="danger-zone">
          <IntercomButton variant="ghost" class="delete-button" @click="handleDelete">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            {{ $t('donation.deleteDonation') }}
          </IntercomButton>
        </div>
      </template>

      <!-- Edit Modal -->
      <IntercomModal v-model:show="showEditModal" :title="$t('donation.editDonation')">
        <DonationForm
          :donation="donation"
          :patrons="patrons"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="showEditModal = false"
        />
      </IntercomModal>

      <!-- Print Modal -->
      <IntercomModal
        v-model:show="showPrintModal"
        :title="$t('receipt.printPreview')"
        :style="{ width: '900px', maxWidth: '95vw' }"
      >
        <div class="print-modal-content">
          <div class="print-preview-container">
            <DonationReceiptPrint
              :donation="donation"
              :patron="selectedPatron"
              :business-name="businessName"
              :business-address="trustSettings.trust_address || ''"
              :trust-name="trustSettings.trust_name || ''"
              :trust-pan="trustSettings.trust_pan || ''"
              :trust-80g-registration="trustSettings.trust_80g_registration || ''"
              :trust-80g-din="trustSettings.trust_80g_din || ''"
              :trust-80g-valid-from="trustSettings.trust_80g_valid_from || ''"
              :trust-80g-valid-to="trustSettings.trust_80g_valid_to || ''"
              :trust-12a-number="trustSettings.trust_12a_number || ''"
            />
          </div>
          <div class="print-actions">
            <IntercomButton variant="primary" @click="handlePrint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {{ $t('common.print') }}
            </IntercomButton>
            <IntercomButton variant="ghost" @click="showPrintModal = false">
              {{ $t('common.close') }}
            </IntercomButton>
          </div>
        </div>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDialog } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomBadge,
  IntercomModal,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import DonationForm from '../components/DonationForm.vue'
import DonationReceiptPrint from '../components/print/DonationReceiptPrint.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const dialog = useDialog()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getDonation, updateDonation, deleteDonation, listPatrons, getKVSettings } = useSeva()
const { navSections } = useNavigation()

// State
const donation = ref(null)
const patrons = ref([])
const trustSettings = ref({})
const isLoading = ref(false)
const isSaving = ref(false)
const showEditModal = ref(false)
const showPrintModal = ref(false)

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

const formatPaymentMode = (mode) => {
  const modes = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', dd: 'Demand Draft', online: 'Online' }
  return modes[mode] || mode
}

const getTypeVariant = (type) => {
  switch (type) {
    case 'money': return 'success'
    case 'in_kind': return 'info'
    case 'volunteer': return 'warning'
    default: return 'default'
  }
}

const fetchDonationData = async () => {
  const donationId = route.params.id
  isLoading.value = true
  try {
    const response = await getDonation(donationId)
    donation.value = response?.donation || response?.data?.donation || response?.data || response
  } catch (error) {
    console.error('Failed to fetch donation:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

const fetchPatrons = async () => {
  try {
    const response = await listPatrons({ limit: 100 })
    patrons.value = response?.data || []
  } catch (error) {
    console.error('Failed to fetch patrons:', error)
  }
}

const fetchTrustSettings = async () => {
  try {
    const response = await getKVSettings()
    trustSettings.value = response?.data || response || {}
  } catch (error) {
    console.error('Failed to fetch trust settings:', error)
  }
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    await updateDonation(donation.value.id, formData)
    toast.success(t('success.donationUpdated'))
    showEditModal.value = false
    fetchDonationData()
  } catch (error) {
    console.error('Failed to save donation:', error)
    toast.error(t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const handleDelete = () => {
  dialog.warning({
    title: t('common.confirm'),
    content: t('donation.deleteConfirm'),
    positiveText: t('common.delete'),
    negativeText: t('common.cancel'),
    onPositiveClick: async () => {
      try {
        await deleteDonation(donation.value.id)
        toast.success(t('success.donationDeleted'))
        router.push('/donations')
      } catch (error) {
        console.error('Failed to delete donation:', error)
        toast.error(t('errors.deleteFailed'))
      }
    },
  })
}

const handlePrint = () => {
  window.print()
}

const selectedPatron = computed(() => {
  if (!donation.value || !patrons.value.length) return null
  return patrons.value.find(p => p.id === donation.value.patron_id) || null
})

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchDonationData()
  fetchPatrons()
  fetchTrustSettings()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.donation-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-button {
  margin-bottom: var(--space-4);
}

.loading-state {
  text-align: center;
  padding: var(--space-12);
  color: var(--color-gray-500);
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-6);
  flex-wrap: wrap;
  gap: var(--space-4);
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

/* Stats Row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.stat-card {
  padding: var(--space-4);
  text-align: center;
}

.stat-card.primary {
  background: var(--color-blue-50, #eff6ff);
  border-color: var(--color-blue-200, #bfdbfe);
}

.stat-card.primary .stat-value {
  color: var(--color-blue-700, #1d4ed8);
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-1);
}

.stat-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.receipt-value {
  font-size: var(--font-size-base);
  word-break: break-word;
}

/* Patron Card */
.patron-card {
  margin-bottom: var(--space-4);
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.patron-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: var(--color-gray-300);
}

.patron-card-content {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.patron-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--color-blue-100, #dbeafe);
  color: var(--color-blue-600, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
}

.patron-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.patron-label {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.patron-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.patron-arrow {
  color: var(--color-gray-400);
  flex-shrink: 0;
}

/* Details Card */
.details-card {
  margin-bottom: var(--space-4);
}

.details-title {
  margin: 0 0 var(--space-4) 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.detail-row {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  width: 140px;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  color: var(--color-charcoal);
}

/* Danger Zone */
.danger-zone {
  margin-top: var(--space-8);
  padding-top: var(--space-6);
  border-top: 1px solid var(--color-gray-200);
}

.delete-button {
  color: var(--color-red-600, #dc2626) !important;
}

.delete-button:hover {
  background: var(--color-red-50, #fef2f2) !important;
}

/* Print modal styles */
.print-modal-content {
  padding: var(--space-2);
}

.print-preview-container {
  background: #f5f5f5;
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  margin-bottom: var(--space-4);
  max-height: 55vh;
  overflow: auto;
  display: flex;
  justify-content: center;
}

.print-actions {
  display: flex;
  justify-content: center;
  gap: var(--space-3);
}

.print-actions button {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* Print media queries */
@media print {
  .donation-detail-view,
  .back-button,
  .page-header,
  .stats-row,
  .patron-card,
  .details-card,
  .danger-zone,
  .print-actions,
  nav,
  aside,
  header {
    display: none !important;
  }

  .print-preview-container {
    max-height: none;
    overflow: visible;
    padding: 0;
    background: white;
    border-radius: 0;
  }

  body {
    margin: 0;
    padding: 0;
  }
}

@media (max-width: 768px) {
  .donation-detail-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .print-actions {
    flex-direction: column;
  }

  .print-actions button {
    width: 100%;
    justify-content: center;
  }
}
</style>
